import { useEffect, useState } from "react";
import { collection, query, where, getDocs, addDoc, deleteDoc, doc, updateDoc, orderBy, serverTimestamp } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { Star, Plus, Trash2, ChevronLeft, Play, ArrowUp, ArrowDown } from "lucide-react";
import { Link } from "react-router-dom";

export default function AdminFeatured() {
  const [featured, setFeatured] = useState([]);
  const [content, setContent] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [featuredSnap, contentSnap] = await Promise.all([
        getDocs(query(collection(db, "featured"), orderBy("order", "asc"))),
        getDocs(query(collection(db, "content"), where("status", "==", "approved"), orderBy("createdAt", "desc"))),
      ]);
      setFeatured(featuredSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setContent(contentSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const addFeatured = async () => {
    if (!selectedId) return;
    setAdding(true);
    try {
      const contentItem = content.find((c) => c.id === selectedId);
      const newOrder = featured.length;
      const newDoc = await addDoc(collection(db, "featured"), {
        contentId: selectedId,
        contentTitle: contentItem?.title || "",
        contentThumbnail: contentItem?.thumbnailURL || "",
        type: "content",
        order: newOrder,
        active: true,
        createdAt: serverTimestamp(),
      });
      setFeatured((f) => [...f, { id: newDoc.id, contentId: selectedId, contentTitle: contentItem?.title || "", contentThumbnail: contentItem?.thumbnailURL || "", order: newOrder, active: true }]);
      setSelectedId("");
    } catch (e) { console.error(e); }
    setAdding(false);
  };

  const removeFeatured = async (id) => {
    await deleteDoc(doc(db, "featured", id));
    setFeatured((f) => f.filter((x) => x.id !== id));
  };

  const toggleActive = async (id, active) => {
    await updateDoc(doc(db, "featured", id), { active: !active });
    setFeatured((f) => f.map((x) => x.id === id ? { ...x, active: !active } : x));
  };

  const moveUp = async (idx) => {
    if (idx === 0) return;
    const updated = [...featured];
    [updated[idx - 1], updated[idx]] = [updated[idx], updated[idx - 1]];
    for (let i = 0; i < updated.length; i++) {
      updated[i] = { ...updated[i], order: i };
      await updateDoc(doc(db, "featured", updated[i].id), { order: i });
    }
    setFeatured(updated);
  };

  return (
    <div data-testid="admin-featured" className="space-y-5">
      <div className="flex items-center gap-3">
        <Link to="/admin" className="text-zinc-500 hover:text-white text-sm flex items-center gap-1"><ChevronLeft size={16} /> Admin</Link>
        <h2 className="text-xl font-black text-white flex items-center gap-2">
          <Star size={18} className="text-yellow-400" /> Featured Content
        </h2>
      </div>

      {/* Add featured */}
      <div className="bg-[#111] border border-white/5 rounded-2xl p-4 flex items-center gap-3">
        <select value={selectedId} onChange={(e) => setSelectedId(e.target.value)}
          className="flex-1 bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#00ff77]/50">
          <option value="">Select approved content to feature...</option>
          {content.filter((c) => !featured.some((f) => f.contentId === c.id)).map((c) => (
            <option key={c.id} value={c.id}>{c.title}</option>
          ))}
        </select>
        <button onClick={addFeatured} disabled={!selectedId || adding} data-testid="add-featured-btn"
          className="flex items-center gap-2 bg-[#00ff77] text-black font-bold text-sm px-4 py-2 rounded-full hover:bg-[#00e66b] transition-colors disabled:opacity-50">
          <Plus size={14} /> Feature
        </button>
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-16 bg-[#111] rounded-xl animate-pulse" />)}</div>
      ) : featured.length === 0 ? (
        <p className="text-zinc-500 text-sm py-8 text-center">No featured content yet. Add some above.</p>
      ) : (
        <div className="space-y-2">
          {featured.map((item, idx) => (
            <div key={item.id} data-testid={`featured-item-${item.id}`}
              className="flex items-center gap-3 bg-[#111] border border-white/5 rounded-xl p-3">
              <div className="flex flex-col gap-0.5">
                <button onClick={() => moveUp(idx)} disabled={idx === 0} className="text-zinc-600 hover:text-white disabled:opacity-20"><ArrowUp size={12} /></button>
                <button onClick={() => moveUp(idx + 1)} disabled={idx === featured.length - 1} className="text-zinc-600 hover:text-white disabled:opacity-20"><ArrowDown size={12} /></button>
              </div>
              <span className="text-zinc-600 text-xs w-4 text-center">{idx + 1}</span>
              <div className="w-14 h-10 rounded-lg bg-[#1a1a1a] overflow-hidden flex-shrink-0">
                {item.contentThumbnail ? (
                  <img src={item.contentThumbnail} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><Play size={10} className="text-zinc-600" /></div>
                )}
              </div>
              <p className="flex-1 text-white text-sm truncate">{item.contentTitle || item.contentId}</p>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onClick={() => toggleActive(item.id, item.active)}
                  className={`text-xs px-2 py-1 rounded-full border transition-colors ${item.active ? "text-[#00ff77] border-[#00ff77]/30 bg-[#00ff77]/10" : "text-zinc-500 border-white/10"}`}>
                  {item.active ? "Live" : "Hidden"}
                </button>
                <button onClick={() => removeFeatured(item.id)} data-testid={`remove-featured-${item.id}`}
                  className="text-zinc-500 hover:text-red-400 p-1 rounded-lg hover:bg-red-400/10 transition-colors">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
